import React, { useEffect } from 'react';
import HandlesLayer from '../../FlowchartView/components/HandlesLayer';
import { ArrowheadDefs, ConnectionPreview } from '../../FlowchartView/components/EdgeRenderer';
import FlowchartHints from '../../FlowchartView/components/FlowchartHints';
import { useBoardConnections } from '../hooks/useBoardConnections';
import '../../FlowchartView/styles/hints.css';

/** Hover lives here so the cards and wires are not redrawn just to show a button. */
function BoardHandleButtons({
  tasks,
  graph,
  edges,
  selectedIds,
  wrapRef,
  cameraRef,
  flowRef,
  actions,
}) {
  const cam = cameraRef.current;
  return (
    <HandlesLayer
      menuHandles
      instantHover
      tasks={tasks}
      allTasks={tasks}
      nodePositions={graph.nodePositions}
      nodeDimensions={graph.nodeDimensions}
      edges={edges}
      selectedNodes={selectedIds}
      containerRef={wrapRef}
      pan={cam}
      zoom={cam.zoom}
      isDragging={actions.isConnecting}
      onConnectionDragStart={actions.startConnection}
      onCreateConnectedNode={actions.createConnected}
      onInsertMissionBefore={actions.insertBefore}
      onPasteBeforeHost={actions.pasteBefore}
      checkClipboard={actions.hasClipboardData}
      onCreateCampaign={flowRef.current?.onCreateCampaign}
      onInsertNode={actions.insertOnEdge}
      isMultiCampaignMode={!!flowRef.current?.isMultiCampaignMode}
    />
  );
}

/**
 * Connection dots, the line-insert control, and the options button for a card.
 * The options button opens the card menu. It does not drag the mission.
 */
export default function BoardHandles({
  tasks,
  graph,
  edges,
  selectedIds,
  wrapRef,
  worldRef,
  cameraRef,
  flowRef,
  gestureRef,
  onSelectionChangeRef,
  menuActionsRef,
  tidyFocusRef,
  tidySettledRef,
}) {
  const actions = useBoardConnections({
    tasks,
    graph,
    selectedIds,
    wrapRef,
    worldRef,
    cameraRef,
    flowRef,
    gestureRef,
    onSelectionChangeRef,
    tidyFocusRef,
    tidySettledRef,
  });
  if (menuActionsRef) menuActionsRef.current = actions;

  useEffect(() => {
    const world = worldRef.current;
    if (!world) return undefined;
    const cam = cameraRef.current;
    world.dispatchEvent(new CustomEvent('panupdate', {
      detail: { x: cam.x, y: cam.y, zoom: cam.zoom },
    }));
    return undefined;
  }, [worldRef, cameraRef]);

  const cam = cameraRef.current;

  return (
    <>
      <BoardHandleButtons
        tasks={tasks}
        graph={graph}
        edges={edges}
        selectedIds={selectedIds}
        wrapRef={wrapRef}
        cameraRef={cameraRef}
        flowRef={flowRef}
        actions={actions}
      />
      {actions.isConnecting && <FlowchartHints isConnecting />}
      {actions.isConnecting && (
        <div
          className="board-view__connection"
          style={{ transform: `translate(${cam.x}px, ${cam.y}px) scale(${cam.zoom})` }}
        >
          <svg className="flowchart__edges flowchart__edges--preview" overflow="visible">
            <ArrowheadDefs />
            <ConnectionPreview
              connectingFrom={actions.connectingFrom}
              connectingTarget={actions.connectingTarget}
              connectingBranch={actions.connectingBranch}
              connectingStartPos={actions.connectingStartPos}
              nodePositions={graph.nodePositions}
              nodeDimensions={graph.nodeDimensions}
              mousePos={actions.mousePos}
              tasks={tasks}
              lineRef={actions.lineRef}
              endRef={actions.endRef}
            />
          </svg>
        </div>
      )}
    </>
  );
}
